import React, { useEffect, useState } from "react";
import api from "@/api";
import { WeightHistoryChart, VolumeByWeekChart } from "@/components/Charts";

const BUCKETS = [
  { k: "week", label: "Week" },
  { k: "month", label: "Month" },
  { k: "year", label: "Year" },
  { k: "allTime", label: "All-time" },
];

function fmtDuration(sec) {
  if (!sec) return "0m";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function Progress() {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("week");

  useEffect(() => {
    api.get("/progress").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-6 text-t3">Loading…</div>;
  const b = data[bucket] || {};

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-5 pt-10 pb-8">
      <div className="overline">Progress</div>
      <h1 className="font-display text-3xl uppercase mt-2">Proof.</h1>

      <div className="flex gap-2 mt-6 overflow-x-auto">
        {BUCKETS.map((x) => (
          <button key={x.k} onClick={() => setBucket(x.k)} className={`chip ${bucket === x.k ? "selected" : ""}`} data-testid={`prog-bucket-${x.k}`}>{x.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <Stat k="Workouts" v={b.workouts ?? 0} />
        <Stat k="Time trained" v={fmtDuration(b.durationSec)} />
        <Stat k="Volume" v={`${((b.volumeKg || 0) / 1000).toFixed(2)} t`} />
        <Stat k="Avg effort" v={b.avgRpe ? `${b.avgRpe}/5` : "—"} />
      </div>

      {/* Volume trend */}
      <div className="mt-4 card p-4" data-testid="volume-trend">
        <div className="overline">Volume · last 12 weeks</div>
        <div className="mt-3">
          <VolumeByWeekChart data={data.volumeByWeek} />
        </div>
      </div>

      {/* Weight trend */}
      <div className="mt-4 card p-4" data-testid="weight-trend">
        <div className="overline">Bodyweight</div>
        <div className="mt-3">
          <WeightHistoryChart data={data.weightHistory} />
        </div>
      </div>

      {/* PRs */}
      <div className="mt-6 card p-4" data-testid="prs-card">
        <div className="overline">Personal records</div>
        <div className="mt-3 space-y-2 text-sm">
          <PRRow k="Heaviest set" v={data.prs.heaviest ? `${data.prs.heaviest.w} kg · ${data.prs.heaviest.exerciseKey}` : "—"} />
          <PRRow k="Biggest session" v={data.prs.biggestVolume ? `${data.prs.biggestVolume.v} kg · ${data.prs.biggestVolume.focus}` : "—"} />
          <PRRow k="Best est 1RM" v={data.prs.best1RM ? `${data.prs.best1RM.est} kg · ${data.prs.best1RM.exerciseKey}` : "—"} />
        </div>
      </div>

      {/* Key lifts */}
      <div className="mt-4 card p-4" data-testid="key-lifts">
        <div className="overline">Key lifts</div>
        <div className="mt-3 space-y-2 text-sm">
          {data.keyLifts.length === 0 ? <div className="text-t3">Log a session to see progress.</div> : data.keyLifts.map((kl) => (
            <div key={kl.exerciseKey} className="flex justify-between items-baseline">
              <div>
                <div className="uppercase font-display">{kl.name}</div>
                {kl.gain != null && kl.startW != null && (
                  <div className="text-t3 text-xs">from {kl.startW} kg · {kl.gain >= 0 ? "+" : ""}{kl.gain}kg</div>
                )}
              </div>
              <div className="font-mono tabular text-lime">{kl.weight ?? "—"} kg</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bodyweight multiplier */}
      <div className="mt-4 card p-4" data-testid="bw-mult">
        <div className="overline">Bodyweight multiplier</div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <Kv k="Squat" v={data.bodyweightMultiplier?.squat ? `${data.bodyweightMultiplier.squat}×` : "—"} />
          <Kv k="Bench" v={data.bodyweightMultiplier?.bench ? `${data.bodyweightMultiplier.bench}×` : "—"} />
        </div>
      </div>

      {/* Event log */}
      <div className="mt-4 card p-4" data-testid="event-log">
        <div className="overline">Activity</div>
        <div className="mt-3 space-y-2 text-xs max-h-64 overflow-y-auto">
          {data.events?.length === 0 ? <div className="text-t3">No events yet.</div> : data.events.slice(0, 40).map((e) => (
            <div key={`${e.ts}-${e.type}`} className="flex justify-between text-t2">
              <span>{e.type}</span>
              <span className="text-t3 tabular font-mono">{e.date} {e.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }) { return <div className="card p-3"><div className="overline">{k}</div><div className="font-display text-2xl mt-1 tabular">{v}</div></div>; }
function Kv({ k, v }) { return <div><div className="overline">{k}</div><div className="font-display text-xl mt-1 tabular text-lime">{v}</div></div>; }
function PRRow({ k, v }) { return <div className="flex justify-between"><span className="text-t2">{k}</span><span className="font-mono tabular">{v}</span></div>; }
